import { Keyword } from '../types';
import { supabase } from './supabase';

export interface KeywordIdea {
  keyword: string;
  avgMonthlySearches: number;
  competition: 'LOW' | 'MEDIUM' | 'HIGH';
  competitionIndex: number;
  lowTopPageBid: number;
  highTopPageBid: number;
  relevance: number;
}

export interface KeywordResearchResult {
  keywords: KeywordIdea[];
  error: string | null;
}

export const keywordService = {
  async getKeywordsByWebsite(websiteId: string): Promise<{ keywords: Keyword[]; error: string | null }> {
    try {
      // First get all URLs for this website
      const { data: urlsData, error: urlsError } = await supabase
        .from('urls')
        .select('url_id')
        .eq('website_id', websiteId);
      
      if (urlsError) {
        return { keywords: [], error: urlsError.message };
      }
      
      const urlIds = (urlsData || []).map(u => u.url_id);
      
      // Then get keywords for these URLs
      const { data, error } = await supabase
        .from('keywords')
        .select('*')
        .in('url_id', urlIds)
        .order('keyword');

      if (error) {
        return { keywords: [], error: error.message };
      }

      const keywords: Keyword[] = data || [];

      return { keywords, error: null };
    } catch (error: any) {
      return { keywords: [], error: error.message };
    }
  },

  async getKeyword(keywordText: string): Promise<{ keyword: Keyword | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('keywords')
        .select('*')
        .eq('keyword', keywordText)
        .single();

      if (error) {
        return { keyword: null, error: error.message };
      }

      return { keyword: data, error: null };
    } catch (error: any) {
      return { keyword: null, error: error.message };
    }
  },

  async createKeyword(
    keywordData: { keyword: string; url_id?: string | null; is_primary?: boolean }
  ): Promise<{ keyword: Keyword | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('keywords')
        .insert([
          {
            keyword: keywordData.keyword,
            url_id: keywordData.url_id || null,
            is_primary: keywordData.is_primary ? 1 : 0,
            last_updated: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) {
        return { keyword: null, error: error.message };
      }

      return { keyword: data, error: null };
    } catch (error: any) {
      return { keyword: null, error: error.message };
    }
  },

  async updateKeyword(
    keywordText: string,
    keywordData: { url_id?: string | null; is_primary?: boolean }
  ): Promise<{ keyword: Keyword | null; error: string | null }> {
    try {
      const updateData: any = {
        last_updated: new Date().toISOString(),
      };
      
      if (keywordData.url_id !== undefined) updateData.url_id = keywordData.url_id;
      if (keywordData.is_primary !== undefined) updateData.is_primary = keywordData.is_primary ? 1 : 0;
      
      const { data, error } = await supabase
        .from('keywords')
        .update(updateData)
        .eq('keyword', keywordText)
        .select()
        .single();

      if (error) {
        return { keyword: null, error: error.message };
      }

      return { keyword: data, error: null };
    } catch (error: any) {
      return { keyword: null, error: error.message };
    }
  },

  async deleteKeyword(keywordText: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.from('keywords').delete().eq('keyword', keywordText);

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async bulkCreateKeywords(
    keywords: Array<{ 
      keyword: string; 
      url_id?: string | null; 
      is_primary?: boolean;
      search_volume?: number | null;
      website_id?: string;
    }>
  ): Promise<{ count: number; error: string | null }> {
    try {
      const insertData = keywords.map((k) => ({
        keyword: k.keyword,
        url_id: k.url_id!, // Force non-null since we filter out nulls
        is_primary: k.is_primary ? 1 : 0,
        last_updated: new Date().toISOString(),
        search_volume: k.search_volume || null,
      })).filter(item => item.url_id !== null);

      console.log('Inserting keywords data:', insertData);

      // Use backend API instead of direct Supabase call
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? '' // Empty string means same origin in production
        : (process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:3001');
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${baseUrl}/api/keywords/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ keywords: insertData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to save keywords' }));
        console.error('Backend error');
        return { count: 0, error: errorData.message || 'Failed to save keywords' };
      }

      const result = await response.json();
      console.log('Keywords saved successfully:', result);
      return { count: result.count || insertData.length, error: null };
    } catch (error: any) {
      console.error('Exception in bulkCreateKeywords');
      return { count: 0, error: error.message };
    }
  },

  // Google Ads API Integration Methods
  async getKeywordIdeas(
    seedKeywords: string[],
    country: string,
    language: string = 'en'
  ): Promise<KeywordResearchResult> {
    try {
      const baseUrl = process.env.NODE_ENV === 'production'
        ? '' // Empty string means same origin in production
        : (process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:3001');

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${baseUrl}/api/keywords/ideas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          seedKeywords,
          country,
          language,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to connect to backend' }));
        throw new Error(errorData.message || 'Failed to fetch keyword ideas');
      }

      const data = await response.json();
      
      // Transform the response to our KeywordIdea format
      const keywords: KeywordIdea[] = data.keywords.map((kw: any) => ({
        keyword: kw.text || kw.keyword,
        avgMonthlySearches: kw.avgMonthlySearches || 0,
        competition: mapCompetition(kw.competition),
        competitionIndex: kw.competitionIndex || 0,
        lowTopPageBid: kw.lowTopOfPageBid || 0,
        highTopPageBid: kw.highTopOfPageBid || 0,
        relevance: kw.relevanceScore || 0.5,
      }));

      return { keywords, error: null };
    } catch (error) {
      console.error('Error fetching keyword ideas:', error);
      return {
        keywords: [],
        error: error instanceof Error ? error.message : 'Failed to fetch keyword ideas',
      };
    }
  },

  async getKeywordMetrics(
    keywords: string[],
    country: string,
    language: string = 'en'
  ): Promise<KeywordResearchResult> {
    try {
      const baseUrl = process.env.NODE_ENV === 'production'
        ? '' // Empty string means same origin in production
        : (process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:3001');

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${baseUrl}/api/keywords/metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          keywords,
          country,
          language,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch keyword metrics');
      }

      const data = await response.json();
      
      const keywordMetrics: KeywordIdea[] = data.metrics.map((metric: any) => ({
        keyword: metric.keyword,
        avgMonthlySearches: metric.avgMonthlySearches || 0,
        competition: mapCompetition(metric.competition),
        competitionIndex: metric.competitionIndex || 0,
        lowTopPageBid: metric.lowTopOfPageBid || 0,
        highTopPageBid: metric.highTopOfPageBid || 0,
        relevance: 1.0, // Metrics endpoint doesn't provide relevance
      }));

      return { keywords: keywordMetrics, error: null };
    } catch (error) {
      console.error('Error fetching keyword metrics:', error);
      return {
        keywords: [],
        error: error instanceof Error ? error.message : 'Failed to fetch keyword metrics',
      };
    }
  },

  // Store keyword research results in database for historical tracking
  async saveKeywordResearch(
    websiteId: string,
    keywords: KeywordIdea[],
    searchType: 'single' | 'bulk',
    seedKeywords: string[]
  ): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // For now, just log the research data since the table doesn't exist yet
      console.log('Would save keyword research:', {
        website_id: websiteId,
        user_id: user.id,
        search_type: searchType,
        seed_keywords: seedKeywords,
        results_count: keywords.length,
      });

      // TODO: Create keyword_research_history table in Supabase
      // const { error } = await supabase
      //   .from('keyword_research_history')
      //   .insert(researchData);
    } catch (error) {
      console.error('Error saving keyword research:', error);
      // Don't throw - this is optional functionality
    }
  },

  // Get historical keyword research for a website
  async getKeywordResearchHistory(websiteId: string): Promise<any[]> {
    try {
      // TODO: Create keyword_research_history table in Supabase
      console.log('Would fetch keyword research history for website:', websiteId);
      return [];
    } catch (error) {
      console.error('Error fetching keyword research history:', error);
      return [];
    }
  },
};

// Helper function to map competition values
function mapCompetition(competition: string | number): 'LOW' | 'MEDIUM' | 'HIGH' {
  if (typeof competition === 'string') {
    const upperComp = competition.toUpperCase();
    if (upperComp === 'LOW' || upperComp === 'MEDIUM' || upperComp === 'HIGH') {
      return upperComp as 'LOW' | 'MEDIUM' | 'HIGH';
    }
  }
  
  // If it's a number or unknown format, map based on value
  const compValue = typeof competition === 'number' ? competition : 0.5;
  if (compValue < 0.33) return 'LOW';
  if (compValue < 0.67) return 'MEDIUM';
  return 'HIGH';
}